import * as React from 'react';
import { useSelector } from 'react-redux';

import { PassengerFragment } from '@websky/graphql';
import { getOrder } from '../../../store/order/selectors';
import ExchangeCheckoutStatus from '../../../../Modules/Exchange/components/Exchange/ExchangeCheckoutStatus/ExchangeCheckoutStatus';
import ParentOrderStatus from '../ParentOrderStatus/ParentOrderStatus';
import ChangePaxOrderStatus from '../ChangePaxOrderStatus/ChangePaxOrderStatus';
import { getTravellerName } from '../../../../utils';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, useTheme } from '../../../../theme';
import { RefundOrderRequestStatus } from '@features/refund/refund-request-status';
import { RefundServicesStatus } from '@features/refund-services';

interface Props {
	goToRefund: () => void;
	goToExchange: () => void;
	goToChangePassenger: (passengerId: string) => void;
}

const OrderExareStatus: React.FC<Props> = ({ goToRefund, goToExchange, goToChangePassenger }) => {
	const { t } = useTranslation();
	const { OrderExareStatus: theme } = useTheme('Checkout');
	const order = useSelector(getOrder);

	const showRefundStatus = !!order.exareInfo.lastRefund;
	const showExchangeStatus = !!order.exareInfo.lastExchange;
	const showRefundServicesStatus = !!order.exareInfo.lastGdsServicesRefund;

	const splittedOrderId = React.useMemo(() => {
		if (order?.splittedOrdersIds?.length) {
			return order.splittedOrdersIds[order.splittedOrdersIds.length - 1];
		}

		return null;
	}, [order?.splittedOrdersIds]);

	const [refundPassengerNames, exchangePassengerNames] = React.useMemo(() => {
		let refundNames: string;
		let exchangeNames: string;

		if (order?.exareInfo) {
			if (order.exareInfo.lastRefund?.travellers?.length) {
				refundNames = order.exareInfo.lastRefund.travellers
					.map((traveller, index) => getTravellerName(traveller, t('passengerNamePattern')))
					.join(', ');
			}

			if (order.exareInfo.lastExchange?.travellers?.length) {
				exchangeNames = order.exareInfo.lastExchange.travellers
					.map(passenger => getTravellerName(passenger as PassengerFragment))
					.join(', ');
			}
		}

		return [refundNames, exchangeNames];
	}, [order?.exareInfo]);

	const goToChangePassengerHandler = () => {
		if (order.exareInfo.lastChangeTravellerDataOrder?.travellers?.length) {
			goToChangePassenger(order.exareInfo.lastChangeTravellerDataOrder.travellers[0].id);
		}
	};

	return (
		<ThemeProvider
			value={{
				BaseComponents: {
					Notification: {
						wrapper: theme.notification__wrapper,
						global: theme.notification__global
					}
				}
			}}
		>
			<RefundOrderRequestStatus
				show={showRefundStatus}
				status={order.exareInfo.lastRefund?.status}
				refundPrice={order.exareInfo.lastRefund?.prices.priceToPay}
				passengersNames={refundPassengerNames}
				splittedOrderId={splittedOrderId}
			/>

			<RefundServicesStatus
				show={showRefundServicesStatus}
				status={order.exareInfo.lastGdsServicesRefund?.status}
				orderId={order.id}
			/>

			<ExchangeCheckoutStatus
				show={showExchangeStatus}
				splittedOrderId={splittedOrderId}
				status={order.exareInfo.lastExchange?.status}
				timelimit={order.exareInfo.lastExchange?.timelimit}
				passengersNames={exchangePassengerNames}
				onGoToTicket={goToExchange}
			/>

			<ParentOrderStatus show={!!order.parentOrderId} splittedOrderId={order.parentOrderId} />

			{order.exareInfo?.lastChangeTravellerDataOrder && (
				<ChangePaxOrderStatus
					order={order}
					lastChangePaxOrder={order.exareInfo.lastChangeTravellerDataOrder}
					onGoToTicket={goToChangePassengerHandler}
				/>
			)}
		</ThemeProvider>
	);
};

export default OrderExareStatus;
